import type { FlowCardDefinition } from '../types';

const operatorOptions = [
	{ value: 'eq', label: 'Equals' },
	{ value: 'ne', label: 'Does not equal' },
	{ value: 'lt', label: 'Less than' },
	{ value: 'gt', label: 'Greater than' },
	{ value: 'le', label: 'Less than or equal' },
	{ value: 'ge', label: 'Greater than or equal' },
];

export const variableConditionCard: FlowCardDefinition = {
	type: 'variableCondition',
	version: '1.0.0',
	domain: 'core',
	role: 'condition',
	category: 'specialCards',
	label: 'Variable Check',
	description: 'Branch on the value of a named Flow variable',
	icon: 'fa-code',
	color: '#f97316',
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		required: ['variableName', 'operator'],
		properties: {
			variableName: { type: 'string', minLength: 1 },
			operator: {
				type: 'string',
				enum: operatorOptions.map((option) => option.value),
			},
			variableValue: { type: 'string' },
		},
	},
	fields: [
		{
			path: 'variableName',
			label: 'Variable name',
			type: 'text',
			placeholder: 'e.g. mode',
		},
		{
			path: 'operator',
			label: 'Comparison',
			type: 'select',
			options: operatorOptions,
		},
		{
			path: 'variableValue',
			label: 'Value',
			type: 'text',
			placeholder: 'e.g. away',
		},
	],
	ports: { sources: ['true', 'false'] },
	defaults: () => ({
		variableName: '',
		operator: 'eq',
		variableValue: '',
		cardVersion: '1.0.0',
	}),
	summary: (data) => {
		const name = String(data.variableName || '').trim();
		if (!name) return 'Choose a variable';
		const symbols: Record<string, string> = {
			eq: '=',
			ne: '≠',
			lt: '<',
			gt: '>',
			le: '≤',
			ge: '≥',
		};
		return `${name} ${symbols[data.operator] || data.operator || '='} ${String(data.variableValue ?? '')}`;
	},
	validate: (data) =>
		String(data.variableName || '').trim()
			? []
			: [{ path: 'variableName', message: 'Enter a variable name' }],
	compile: (node) => {
		const name = String(node.data.variableName || '').trim();
		if (!name) return null;
		const raw = node.data.variableValue;
		const value =
			raw !== '' && raw != null && !Number.isNaN(Number(raw)) ? Number(raw) : String(raw ?? '');
		return {
			r: `var:${name}`,
			cmpop: String(node.data.operator || 'eq'),
			cmpval: value,
			_gui_purpose: 'condition',
			_flow_card: 'variableCondition',
			_flow_card_version: '1.0.0',
		};
	},
	migrate: (data) => ({
		...data,
		variableName: data.variableName ?? (data.variable as any)?.name ?? '',
		variableValue: data.variableValue ?? (data.variable as any)?.value ?? '',
		operator: data.operator ?? 'eq',
	}),
};
